import type { TranslationVariables, AllTranslationKeys } from 'shared/services/TranslatorNext/TranslationService';
import 'shared/services/TranslatorNext/TranslationService';
import i18next from 'i18next';

interface PostProcessor {
  name: string;
  type: 'postProcessor';
  process: (value: string, key: AllTranslationKeys | AllTranslationKeys[], options: TranslationVariables, translator: { language?: string }) => string;
}

export const Upper: PostProcessor = {
  name: 'upper',
  type: 'postProcessor',
  process: (v: string, _k: AllTranslationKeys | AllTranslationKeys[], _o: TranslationVariables, tr: { language?: string }) => v.toLocaleUpperCase(tr?.language)
};

export const Lower: PostProcessor = {
  name: 'lower',
  type: 'postProcessor',
  process: (v: string, _k: AllTranslationKeys | AllTranslationKeys[], _o: TranslationVariables, tr: { language?: string }) => v.toLocaleLowerCase(tr?.language)
};

export const CapFirst: PostProcessor = {
  name: 'capFirst',
  type: 'postProcessor',
  process: (v: string, _k: AllTranslationKeys | AllTranslationKeys[], _o: TranslationVariables, tr: { language?: string }) => v.charAt(0).toLocaleUpperCase(tr?.language) + v.slice(1)
};

export const TitleCase: PostProcessor = {
  name: 'titleCase',
  type: 'postProcessor',
  process: (v: string, _k: AllTranslationKeys | AllTranslationKeys[], _o: TranslationVariables, tr: { language?: string }) => {
    return v.replace(/([A-Za-zÀ-ÖØ-öø-ÿ\p{L}\p{N}])(\S*)/gu, (_match: string, firstChar: string, rest: string) => {
      return firstChar.toLocaleUpperCase(tr?.language) + rest.toLocaleLowerCase(tr?.language);
    });
  }
};

/**
 * StripNoTranslate
 *
 * Post processor to handle inline non-translatable segments inside translation strings.
 * Usage in JSON:
 *   "key": "Welcome to {{u:VWO}} testing platform"
 *
 * Any segment wrapped as {{u:...}} will be preserved as-is (treated as English / non-translated text)
 * after i18next has resolved the main translation.
 */
export const StripNoTranslate: PostProcessor = {
  name: 'stripNoTranslate',
  type: 'postProcessor',
  process: (v: string, _k: AllTranslationKeys | AllTranslationKeys[], _o: TranslationVariables, _tr: { language?: string }) => {
    // Fast-path: only continue if there's at least one {{ ... }} segment.
    if (!v.includes('{{')) {
      return v;
    }
    return v.replace(/\{\{([^}]+)\}\}/g, (fullMatch: string, innerRaw: string) => {
      const inner = innerRaw.trim();

      if (!inner.startsWith('u:')) {
        return fullMatch;
      }

      const content = inner.slice(2).trim(); // remove 'u:' and trim again
      return content;
    });
  }
};

enum REPORTING_MESSAGE {
  TRANSLATION_KEY_MISSING = 'Translation key missing'
}

function captureMessage(message: string, extra: { key: string, value: string, ns?: string, options: TranslationVariables, message: string }) {
  import('@sentry/browser').then(async (raven) => {
    const { $rootScope } = await import('ngImports');
    if ($rootScope.currentTransition?.toState?.name === 'login' || !window.amILoggedIn) {
      return;
    }
    raven.captureMessage(message, {
      extra: {
        ...extra,
        currentLanguage: i18next.language,
        isInitialized: i18next.isInitialized,
        hasResourceBundle: ((): boolean | undefined => {
          try {
            if (!extra?.ns) {
              return undefined;
            }
            const lang = i18next.language || 'en';
            return i18next.hasResourceBundle(lang, extra.ns);
          } catch (_e) {
            return undefined;
          }
        })(),
        exists: ((): boolean | undefined => {
          try {
            return extra?.key ? i18next.exists(extra.key) : undefined;
          } catch (_e) {
            return undefined;
          }
        })()
      }
    });
  });
}

export const ReportMissingKey: PostProcessor = {
  name: 'reportMissingKey',
  type: 'postProcessor',
  process: (value: string, fullKey: AllTranslationKeys | AllTranslationKeys[], options: TranslationVariables) => {
    try {
      let normalizedFullKey = fullKey;
      if (Array.isArray(fullKey)) {
        normalizedFullKey = fullKey[0]!;
      }

      if (typeof normalizedFullKey !== 'string') {
        return value;
      }

      const nsSeparatorIndex = normalizedFullKey.indexOf(':');
      const ns = nsSeparatorIndex >= 0 ? normalizedFullKey.slice(0, nsSeparatorIndex) : '';
      if (!ns) {
        captureMessage(REPORTING_MESSAGE.TRANSLATION_KEY_MISSING, {
            key: normalizedFullKey,
            value,
            message: 'Namespace is missing',
            options
        });
        return value;
      }

      // Skip reporting until i18next is initialized and namespace bundle is available
      const currentLanguage = i18next.language || 'en';
      if (!i18next.isInitialized || !i18next.hasResourceBundle(currentLanguage, ns)) {
        return value;
      }

      const hasCountOpt = options?.hasOwnProperty?.('count') ?? false;

      if (hasCountOpt) {
        const countValue = Number(options?.count);
        const hasContextAlso = options?.hasOwnProperty?.('context') ?? false;
        const ctx = hasContextAlso ? String(options?.context) : undefined;
        const existsWithCount = i18next.exists(
          normalizedFullKey,
          hasContextAlso ? { count: countValue, context: ctx } : { count: countValue }
        );
        if (!existsWithCount) {
          captureMessage(REPORTING_MESSAGE.TRANSLATION_KEY_MISSING, {
            key: normalizedFullKey,
            ns,
            value,
            message: 'Plural/base variant is missing',
            options
          });
        }
        return value;
      }

      const hasContextOpt = options?.hasOwnProperty?.('context') ?? false;

      if (hasContextOpt) {
        const contextValue = options?.context;
        const ctx = String(contextValue);
        const contextualKey = `${normalizedFullKey}${ctx ? `_${ctx}` : ''}`;
        const contextualExists = i18next.exists(contextualKey);

        if (!contextualExists) {
          captureMessage(REPORTING_MESSAGE.TRANSLATION_KEY_MISSING, {
            key: contextualKey,
            ns,
            value,
            message: 'Context variant is missing',
            options
          });
        }
        return value;
      }

      const doesKeyExist = i18next.exists(normalizedFullKey);
      // Report when the key itself is missing
      if (!doesKeyExist) {
        captureMessage(REPORTING_MESSAGE.TRANSLATION_KEY_MISSING, {
          key: normalizedFullKey,
          ns,
          value,
          message: 'Translation key is missing',
          options
        });

        return value;
      }

    } catch (_e) {
      // swallow errors in post-processing path
    }

    return value;
  }
};

const TranslationToKeyMap = new Map<string, {
  key: AllTranslationKeys | AllTranslationKeys[];
  options: TranslationVariables;
}[]>();

// Attach to window for global access
if (typeof window !== 'undefined') {
  window.TranslationToKeyMap = TranslationToKeyMap;
}

export const TranslationToKey: PostProcessor = {
  name: 'translationToKey',
  type: 'postProcessor',
  process: (value, key, options) => {
    const trimmedValue = value.trim();
    if (!TranslationToKeyMap.has(trimmedValue)) {
      TranslationToKeyMap.set(trimmedValue, []);
    }

    const current = TranslationToKeyMap.get(trimmedValue);
    if (current) {
      const normalizedKey = Array.isArray(key) ? key[0]! : key;
      const isAlreadyPresent = current.some(entry => entry.key === normalizedKey);
      if (!isAlreadyPresent) {
        current.push({
          key: normalizedKey,
          options
        });
      }
    }

    return value;
  }
};
